// AuthManager.js

import { EventDispatcher } from "../services/EventDispatcher.js";

export class AuthManager {

    /**
     * @param {Object} config
     * @param {AuthContract} config.authService             - Auth service (login/logout/refresh/user)
     * @param {'cookie'|'bearer'} config.authMethod          - authentication method
     * @param {Storage|null} [config.storage]               - e.g. window.localStorage; when null, auth state is not persisted
     * @param {string} [config.storageKey='auth']           - storage key
     */
    constructor(config = {}) {

        const {
            authService,
            authMethod = 'cookie',
            storage = typeof window !== 'undefined' ? window.localStorage : null,
            storageKey = 'auth',
        } = config;

        if (!authService) throw new Error('AuthManager: "authService" is required.');
        if (typeof authService.login !== 'function'
            || typeof authService.logout !== 'function'
            || typeof authService.refresh !== 'function'
            || typeof authService.getUser !== 'function') {
            throw new Error('AuthManager: "authService" must implement login/logout/refresh/getUser.');
        }

        this.authService = authService;
        this.authMethod = authMethod;
        this.storage = storage;
        this.storageKey = storageKey;

        this.accessToken = null;
        this.refreshToken = null;
        this.expiresAt = null; // timestamp (ms) nebo null
        this.currentUser = null;

        // event handlers
        this._loginEvent = new EventDispatcher();
        this.onLogin = this._loginEvent.subscribeHandler();
        this._logoutEvent = new EventDispatcher();
        this.onLogout = this._logoutEvent.subscribeHandler();
        this._authErrorEvent = new EventDispatcher();
        this.onAuthError = this._authErrorEvent.subscribeHandler();
        this._tokenRefreshedEvent = new EventDispatcher();
        this.onTokenRefreshed = this._tokenRefreshedEvent.subscribeHandler();

        // shared promise pro current refresh
        this._refreshPromise = null;

        this._loadFromStorage();
        this._initializeStorageSync();
    }

    // -----------------------------------------------------
    // Public API
    // -----------------------------------------------------

    /**
     * Log in the user.
     * @param {Object} credentials - typically { username, password } or API-specific credentials
     * @returns {Promise<Object>} - user object if the API returns one
     */
    async login(credentials) {

        const data = await this.authService.login(credentials || {});

        if (this.isBearerMode()) {

            const { accessToken, refreshToken, expiresAt, user } = this._extractTokensFromLogin(data);

            this._setAuth(accessToken, refreshToken, expiresAt, user);

            this._loginEvent.dispatch(user);

            return user;

        }

        let user = data?.user ?? data ?? null;
        if (!user) {
            user = await this._loadUserRaw();
        } else {
            this.currentUser = user;
            this._saveToStorage();
        }

        this._loginEvent.dispatch({
            user,
            remote: false,
        });

        return user;
    }

    /**
     * Log out the user, clear tokens/user info, and notify listeners.
     */
    async logout(options = {}) {

        const {
            remote = false,
        } = options;

        if (!remote) {
            try {
                await this.authService.logout();
            } catch {
                // consume
            }
        }

        this._clearAuth();
        this._logoutEvent.dispatch({
            remote,
        });

    }

    /**
     * Return true when an access token exists and has not expired, if expiry is tracked.
     */
    hasValidToken() {

        if (!this.accessToken) return false;
        if (!this.expiresAt) return true;

        return Date.now() < this.expiresAt;

    }

    /**
     * Return whether the user is considered authenticated.
     */
    isAuthenticated() {

        if (this.isCookieMode()) {
            // Cookie mode has no visible client token.
            return this.currentUser != null;
        }

        return this.hasValidToken();

    }

    getAccessToken() {

        return this.accessToken;

    }

    getRefreshToken() {

        return this.refreshToken;

    }

    getUser() {

        if (!this.isAuthenticated()) {
            return null;
        }
        return this.currentUser;

    }

    /**
     * Ensure a valid access token:
     *  - return it when still valid
     *  - otherwise try refresh
     *  - throw when refresh fails
     */
    async ensureValidToken() {

        if (this.hasValidToken()) {
            return this.accessToken;
        }

        await this.refresh();
        return this.accessToken;

    }

    /**
     * Call /auth/refresh or another configured endpoint and update tokens.
     * Coalesces concurrent refresh calls into one in-flight refresh.
     *
     * @returns {Promise<string>} - new access token
     */
    async refresh() {

        if (this.isCookieMode()) {

            // Cookie mode usually does not need a body token; the server reads the refresh cookie.

            if (this._refreshPromise) return this._refreshPromise;

            this._refreshPromise = this._doCookieRefresh()
                .catch(err => {
                    // Refresh failure means the session is gone; log out locally.
                    awaitPromiseSafe(() => this.logout());
                    this._authErrorEvent.dispatch(err);
                    throw err;
                })
                .finally(() => {
                    this._refreshPromise = null;
                });

            return this._refreshPromise;

        }

        if (!this.refreshToken) {
            // Nothing to refresh.
            this.logout();
            const err = new Error('No refresh token available.');
            this._authErrorEvent.dispatch(err);
            throw err;
        }

        // Reuse the existing in-flight refresh promise.
        if (this._refreshPromise) {
            return this._refreshPromise;
        }

        this._refreshPromise = this._doRefresh()
            .catch(err => {
                // Refresh failure logs out and emits the error.
                this.logout();
                this._authErrorEvent.dispatch(err);
                throw err;
            })
            .finally(() => {
                this._refreshPromise = null;
            });

        return this._refreshPromise;
    }

    /**
     * Return user info.
     * Uses the in-memory cache unless forceReload is true.
     * Calls the user endpoint when needed.
     */
    async refreshUser(forceReload = false) {

        if (!forceReload && this.currentUser) {
            return this.currentUser;
        }

        try {
            if (this.isCookieMode()) {
                await this.refresh();
                return this.currentUser;
            }

            await this.ensureValidToken();
            return await this._loadUserRaw();
        } catch (err) {
            this._authErrorEvent.dispatch(err);
            throw err;
        }

    }

    async _loadUserRaw() {

        this.currentUser = await this.authService.getUser({
            authMethod: this.authMethod,
            accessToken: this.getAccessToken(),
        });
        this._saveToStorage();
        return this.currentUser;

    }

    validateUserPrivileges(requestedPrivileges, item, ownerIdHandler, parentOwnerIdHandler) {

        var user = this.getUser();

        if (!user)
            return false;

        if (user.isSysAdmin === true)
            return true;

        var privileges = user.privileges || [];
        if (!Array.isArray(privileges))
            privileges = [privileges];
        if (privileges.includes('#sysadmin'))
            return true;

        var userid = user.id;

        requestedPrivileges = requestedPrivileges || [];
        if (!Array.isArray(requestedPrivileges))
            requestedPrivileges = [requestedPrivileges];
        if (requestedPrivileges.length === 0)
            return false;

        return requestedPrivileges.some(function (requestedPrivilege) {
            if (Array.isArray(requestedPrivilege)) {
                return requestedPrivilege.every(function (singleRequestedPrivilege) {
                    if (privileges.includes(singleRequestedPrivilege))
                        return true;
                    if (singleRequestedPrivilege === '#owner') {
                        var ownerId = (item && ownerIdHandler && ownerIdHandler(item)) || null;
                        return ownerId === userid;
                    }
                    if (singleRequestedPrivilege === '#parentOwner') {
                        var parentOwnerId = (item && parentOwnerIdHandler && parentOwnerIdHandler(item)) || null;
                        return parentOwnerId === userid;
                    }
                    return false;
                });
            }
            return privileges.includes(requestedPrivilege);
        });

    };

    hasUserRole(roles) {

        let user = this.getUser();

        if (!user)
            return false;

        if (!Array.isArray(roles))
            roles = [roles]

        return user.roles.some(role => roles.includes(role));

    };

    // -----------------------------------------------------
    // Internal logic
    // -----------------------------------------------------

    isBearerMode() { return this.authMethod === 'bearer'; }

    isCookieMode() { return this.authMethod === 'cookie'; }

    _extractTokensFromLogin(data) {

        // Adapt here for API-specific response shapes.
        // Expected shape: { accessToken, refreshToken, expiresAt?, user? }

        if (!data || !data.accessToken) {
            throw new Error('Login response does not contain accessToken.');
        }

        return {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken ?? null,
            // expiresAt is expected to be either a timestamp in ms or an ISO string.
            expiresAt: data.expiresAt ? this._normalizeExpiresAt(data.expiresAt) : null,
            user: data.user ?? null,
        };
    }

    _extractTokensFromRefresh(data) {
        // Adapt here for API-specific response shapes.
        if (!data || !data.accessToken) {
            throw new Error('Refresh response does not contain accessToken.');
        }

        return {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken ?? this.refreshToken,
            expiresAt: data.expiresAt
                ? this._normalizeExpiresAt(data.expiresAt)
                : null
        };
    }

    _normalizeExpiresAt(value) {
        if (typeof value === 'number') {
            // Assume milliseconds.
            return value;
        }

        // Otherwise try Date.parse.
        const ts = Date.parse(value);
        return Number.isNaN(ts) ? null : ts;
    }

    async _doRefresh() {

        const data = await this.authService.refresh({
            authMethod: 'bearer',
            refreshToken: this.refreshToken,
            accessToken: this.getAccessToken(),
        });
        const { accessToken, refreshToken, expiresAt } = this._extractTokensFromRefresh(data);

        this._setAuth(accessToken, refreshToken, expiresAt, this.currentUser);
        this.currentUser = await this._loadUserRaw();
        this._setAuth(accessToken, refreshToken, expiresAt, this.currentUser);
        this._tokenRefreshedEvent.dispatch(accessToken);

        return accessToken;

    }

    async _doCookieRefresh() {

        await this.authService.refresh({
            authMethod: 'cookie',
        });

        // Refresh endpoints often return no body; reload the user after refresh.
        this.currentUser = await this._loadUserRaw();
        this._tokenRefreshedEvent.dispatch(null);

        return null;

    }

    _setAuth(accessToken, refreshToken, expiresAt, user = null) {
        this.accessToken = accessToken;
        this.refreshToken = refreshToken;
        this.expiresAt = expiresAt;
        this.currentUser = user ?? this.currentUser;

        this._saveToStorage();
    }

    _clearAuth() {
        this.accessToken = null;
        this.refreshToken = null;
        this.expiresAt = null;
        this.currentUser = null;

        this._clearStorage();
    }

    _loadFromStorage() {
        if (!this.storage) return;

        try {
            const raw = this.storage.getItem(this.storageKey);
            if (!raw) return;

            const data = JSON.parse(raw);
            this.accessToken = data.accessToken ?? null;
            this.refreshToken = data.refreshToken ?? null;
            this.expiresAt = data.expiresAt ?? null;
            this.currentUser = data.currentUser ?? null;
        } catch {
            // ignorujeme chyby
        }
    }

    _saveToStorage() {
        if (!this.storage) return;

        const data = {
            accessToken: this.accessToken,
            refreshToken: this.refreshToken,
            expiresAt: this.expiresAt,
            currentUser: this.currentUser
        };

        try {
            this.storage.setItem(this.storageKey, JSON.stringify(data));
        } catch {
            // ignorujeme, typicky quota exceeded apod.
        }
    }

    _clearStorage() {
        if (!this.storage) return;
        try {
            this.storage.removeItem(this.storageKey);
        } catch {
            // ignorujeme
        }
    }

    _initializeStorageSync() {

        if (typeof window === 'undefined')
            return;
        if (!this.storage)
            return;
        if (typeof window.addEventListener !== 'function')
            return;

        window.addEventListener('storage', (event) => {

            if (event.storageArea && event.storageArea !== this.storage)
                return;
            if (event.key !== this.storageKey)
                return;

            this._synchronizeFromStorageEvent(event.newValue ?? null);

        });

    }

    _synchronizeFromStorageEvent(rawValue) {

        const previousSnapshot = this._getAuthSnapshot();
        const nextSnapshot = this._parseStoredSnapshot(rawValue);

        this.accessToken = nextSnapshot.accessToken;
        this.refreshToken = nextSnapshot.refreshToken;
        this.expiresAt = nextSnapshot.expiresAt;
        this.currentUser = nextSnapshot.currentUser;

        this._dispatchStorageSyncEvents(previousSnapshot, nextSnapshot);

    }

    _parseStoredSnapshot(rawValue) {

        if (!rawValue) {
            return {
                accessToken: null,
                refreshToken: null,
                expiresAt: null,
                currentUser: null,
            };
        }

        try {
            const data = JSON.parse(rawValue);
            return {
                accessToken: data.accessToken ?? null,
                refreshToken: data.refreshToken ?? null,
                expiresAt: data.expiresAt ?? null,
                currentUser: data.currentUser ?? null,
            };
        } catch {
            return {
                accessToken: null,
                refreshToken: null,
                expiresAt: null,
                currentUser: null,
            };
        }

    }

    _getAuthSnapshot() {
        return {
            accessToken: this.accessToken,
            refreshToken: this.refreshToken,
            expiresAt: this.expiresAt,
            currentUser: this.currentUser,
            isAuthenticated: this.isAuthenticated(),
        };
    }

    _dispatchStorageSyncEvents(previousSnapshot, nextSnapshot) {

        const previousUserId = previousSnapshot.currentUser?.id ?? null;
        const nextUserId = nextSnapshot.currentUser?.id ?? null;
        const wasAuthenticated = previousSnapshot.isAuthenticated;
        const isAuthenticated = this.isAuthenticated();

        if (wasAuthenticated && !isAuthenticated) {
            this._logoutEvent.dispatch({ remote: true });
            return;
        }

        if (!wasAuthenticated && isAuthenticated) {
            this._loginEvent.dispatch({
                user: this.currentUser,
                remote: true,
            });
            return;
        }

        if (
            isAuthenticated
            && (
                previousUserId !== nextUserId
                || previousSnapshot.accessToken !== nextSnapshot.accessToken
                || previousSnapshot.refreshToken !== nextSnapshot.refreshToken
                || previousSnapshot.expiresAt !== nextSnapshot.expiresAt
            )
        ) {
            this._tokenRefreshedEvent.dispatch({
                accessToken: this.accessToken,
                remote: true,
                user: this.currentUser,
            });
        }

    }

    _removeHandler(list, handler) {
        const idx = list.indexOf(handler);
        if (idx >= 0) {
            list.splice(idx, 1);
        }
    }

    // Legacy alias kept for existing module callers.
    async logoutUser() {
        return this.logout();
    }

}

function awaitPromiseSafe(fn) {
    try { return Promise.resolve(fn()); } catch { return Promise.resolve(); }
}
